<template>
  <div class="rack-optimization-calculator" data-theme="v1">
    <!-- Header -->
    <public-lease-audit-header ref="header" title="Cubic Feet + Rack Optimization Calculator" />

    <!-- Main Content -->
    <div class="rack-optimization-calculator__container">
      <!-- Page Header -->
      <!-- <div class="rack-optimization-calculator__page-header">
        <div class="rack-optimization-calculator__header-content">
          <div class="rack-optimization-calculator__header-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
            </svg>
          </div>
          <div>
            <h2 class="rack-optimization-calculator__page-title">Industrial Storage Optimization Calculator</h2>
            <p class="rack-optimization-calculator__page-description">Calculate total cubic feet, usable storage, and rack configuration optimization</p>
          </div>
        </div>
      </div> -->

      <!-- Calculator Form -->
      <div class="rack-optimization-calculator__form-section">
        <form @submit.prevent="calculate">
          <!-- Section A: Building Dimensions -->
          <div class="calculator-section">
            <h3 class="calculator-section__title">A. Building Dimensions (Feet)</h3>
            <div class="calculator-section__grid">
              <div class="form-group">
                <label for="buildingLength">Building Length (ft)</label>
                <input 
                  type="number" 
                  id="buildingLength" 
                  v-model.number="buildingLength" 
                  min="0"
                  step="0.01"
                  required
                  class="form-input"
                  placeholder="e.g., 200"
                />
                <span class="form-help">Total length of the building</span>
              </div>

              <div class="form-group">
                <label for="buildingWidth">Building Width (ft)</label>
                <input 
                  type="number" 
                  id="buildingWidth" 
                  v-model.number="buildingWidth" 
                  min="0"
                  step="0.01"
                  required
                  class="form-input"
                  placeholder="e.g., 150"
                />
                <span class="form-help">Total width of the building</span>
              </div>

              <div class="form-group">
                <label for="ceilingHeight">Ceiling Height (ft)</label>
                <input 
                  type="number" 
                  id="ceilingHeight" 
                  v-model.number="ceilingHeight" 
                  min="0"
                  step="0.01"
                  required
                  class="form-input"
                  placeholder="e.g., 24"
                />
                <span class="form-help">Clear ceiling height</span>
              </div>
            </div>
          </div>

          <!-- Section B: Rack Configuration -->
          <div class="calculator-section">
            <h3 class="calculator-section__title">B. Rack Configuration</h3>
            <div class="calculator-section__grid">
              <div class="form-group">
                <label for="rackHeight">Rack Height</label>
                <select 
                  id="rackHeight" 
                  v-model.number="rackHeight" 
                  required
                  class="form-input"
                >
                  <option value="">Select height...</option>
                  <option :value="48">48 inches (4 ft)</option>
                  <option :value="60">60 inches (5 ft)</option>
                  <option :value="72">72 inches (6 ft)</option>
                </select>
                <span class="form-help">Height of rack units</span>
              </div>

              <div class="form-group">
                <label for="rackDepth">Rack Depth</label>
                <select 
                  id="rackDepth" 
                  v-model.number="rackDepth" 
                  required
                  class="form-input"
                >
                  <option value="">Select depth...</option>
                  <option :value="36">36 inches (3 ft)</option>
                  <option :value="40">40 inches (3.33 ft)</option>
                  <option :value="48">48 inches (4 ft)</option>
                  <option :value="60">60 inches (5 ft)</option>
                </select>
                <span class="form-help">Depth of rack units</span>
              </div>

              <div class="form-group">
                <label for="rackRunLength">Rack Run Length (ft)</label>
                <input 
                  type="number" 
                  id="rackRunLength" 
                  v-model.number="rackRunLength" 
                  min="0"
                  step="0.01"
                  required
                  class="form-input"
                  placeholder="e.g., 100"
                />
                <span class="form-help">Linear feet per rack row</span>
              </div>
            </div>
          </div>

          <!-- Section C: Equipment Type -->
          <div class="calculator-section">
            <h3 class="calculator-section__title">C. Material Handling Equipment</h3>
            <div class="calculator-section__grid calculator-section__grid--single">
              <div class="form-group">
                <label for="equipmentType">Equipment Type</label>
                <select 
                  id="equipmentType" 
                  v-model="equipmentType" 
                  required
                  class="form-input"
                >
                  <option value="">Select equipment...</option>
                  <option value="manual">Manual Pallet Jack (6 ft aisle)</option>
                  <option value="electric">Electric Pallet Jack (8 ft aisle)</option>
                  <option value="reach">Reach Truck (9 ft aisle)</option>
                  <option value="forklift">Standard Forklift (12 ft aisle)</option>
                </select>
                <span class="form-help">Equipment determines required aisle width</span>
              </div>
            </div>
          </div>

          <!-- Calculate Button -->
          <div class="calculator-actions">
            <button type="submit" class="btn-calculate">
              <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
              Calculate Storage Optimization
            </button>
            <button type="button" @click="reset" class="btn-reset">
              Reset
            </button>
          </div>
        </form>
      </div>

      <!-- Results Section -->
      <div v-if="results" class="rack-optimization-calculator__results">
        <h3 class="results-title">Storage Optimization Results</h3>
        
        <!-- Summary Cards -->
        <div class="results-grid">
          <div class="result-card result-card--primary">
            <div class="result-card__icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Building Volume</div>
              <div class="result-card__value">{{ formatNumber(results.buildingVolume) }} ft³</div>
            </div>
          </div>

          <div class="result-card">
            <div class="result-card__icon result-card__icon--success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Usable Storage Volume</div>
              <div class="result-card__value">{{ formatNumber(results.storageVolume) }} ft³</div>
              <div class="result-card__formula">{{ results.numberOfRows }} rows × {{ formatNumber(results.totalLinearFeet) }} ft × {{ formatNumber(results.rackDepthFt) }} ft × {{ formatNumber(results.rackHeightFt) }} ft</div>
            </div>
          </div>

          <div class="result-card">
            <div class="result-card__icon result-card__icon--info">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Storage Utilization</div>
              <div class="result-card__value">{{ formatNumber(results.utilizationPercent) }}%</div>
              <div class="result-card__formula">Storage Volume ÷ Building Volume</div>
            </div>
          </div>

          <div class="result-card">
            <div class="result-card__icon result-card__icon--purple">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Number of Rack Rows</div>
              <div class="result-card__value">{{ results.numberOfRows }}</div>
              <div class="result-card__formula">{{ formatNumber(results.totalLinearFeet) }} total linear feet</div>
            </div>
          </div>
        </div>

        <!-- Intermediate Calculations -->
        <div class="results-breakdown">
          <h4 class="breakdown-title">Configuration Details</h4>
          <div class="breakdown-grid">
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Height:</span>
              <span class="breakdown-value">{{ rackHeight }}" ({{ formatNumber(results.rackHeightFt) }} ft)</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Depth:</span>
              <span class="breakdown-value">{{ rackDepth }}" ({{ formatNumber(results.rackDepthFt) }} ft)</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Equipment Type:</span>
              <span class="breakdown-value">{{ getEquipmentLabel(equipmentType) }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Required Aisle Width:</span>
              <span class="breakdown-value">{{ results.aisleWidth }} ft</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Lane Width (Rack + Aisle):</span>
              <span class="breakdown-value">{{ formatNumber(results.laneWidth) }} ft</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Run Length:</span>
              <span class="breakdown-value">{{ formatNumber(rackRunLength) }} ft</span>
            </div>
          </div>
        </div>

        <!-- Equipment Comparison -->
        <div v-if="comparisonResults.length > 0" class="results-comparison">
          <h4 class="breakdown-title">Equipment Comparison</h4>
          <p class="comparison-description">See how different equipment choices impact your storage capacity</p>
          
          <div class="comparison-table">
            <div class="comparison-header">
              <div class="comparison-cell">Equipment</div>
              <div class="comparison-cell">Aisle Width</div>
              <div class="comparison-cell">Rows</div>
              <div class="comparison-cell">Storage Volume</div>
              <div class="comparison-cell">Utilization</div>
              <div class="comparison-cell">vs Current</div>
            </div>
            <div 
              v-for="comp in comparisonResults" 
              :key="comp.equipment"
              :class="['comparison-row', { 'comparison-row--current': comp.equipment === equipmentType }]"
            >
              <div class="comparison-cell">
                <span class="comparison-equipment">{{ getEquipmentLabel(comp.equipment) }}</span>
              </div>
              <div class="comparison-cell">{{ comp.aisleWidth }} ft</div>
              <div class="comparison-cell">{{ comp.numberOfRows }}</div>
              <div class="comparison-cell">{{ formatNumber(comp.storageVolume) }} ft³</div>
              <div class="comparison-cell">{{ formatNumber(comp.utilizationPercent) }}%</div>
              <div class="comparison-cell">
                <span :class="['comparison-diff', comp.diffClass]">
                  {{ comp.diffLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Modal -->
    <email-modal
      :show="showEmailModal"
      @update:show="showEmailModal = $event"
      @submit="handleEmailSubmit"
      @cancel="handleEmailCancel"
      title="Enter Your Email"
      description="Please provide your email address to view the calculation results."
      calculation="rack-optimization-calculator"
    />
  </div>
</template>

<script>
import EmailModal from '../../../common/EmailModal.vue';
import PublicLeaseAuditHeader from './PublicLeaseAuditHeader.vue';

export default {
  name: 'RackOptimizationCalculator',

  components: {
    EmailModal,
    PublicLeaseAuditHeader
  },

  data() {
    return {
      // Building Dimensions
      buildingLength: null,
      buildingWidth: null,
      ceilingHeight: null,
      
      // Rack Configuration
      rackHeight: '',
      rackDepth: '',
      rackRunLength: null,
      
      // Equipment Type
      equipmentType: '',
      
      results: null,
      comparisonResults: [],
      showEmailModal: false,
      userEmail: null,
      pendingCalculation: false,
      
      // Equipment aisle width mapping
      equipmentAisleWidths: {
        manual: 6,
        electric: 8,
        reach: 9,
        forklift: 12
      }
    };
  },

  mounted() {
    // Check if user info is already stored
    const storedEmail = sessionStorage.getItem('public_view_email');
    const storedName = sessionStorage.getItem('public_view_name');
    const storedPhone = sessionStorage.getItem('public_view_phone');
    
    if (storedEmail) {
      this.userEmail = storedEmail;
      
      // Update header with stored user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = storedEmail;
        if (storedName) {
          this.$refs.header.userName = storedName;
        }
        if (storedPhone) {
          this.$refs.header.userPhone = storedPhone;
        }
      }
    }
  },

  methods: {
    calculate() {
      // Check if email is already provided
      if (!this.userEmail) {
        // Show email modal first
        this.pendingCalculation = true;
        this.showEmailModal = true;
        return;
      }

      // Perform calculation
      this.performCalculation();
    },

    performCalculation() {
      // Step 1: Building Volume
      const buildingVolume = (this.buildingLength || 0) * (this.buildingWidth || 0) * (this.ceilingHeight || 0);
      
      // Step 2: Convert Rack Dimensions
      const rackHeightFt = (this.rackHeight || 0) / 12;
      const rackDepthFt = (this.rackDepth || 0) / 12;
      
      // Step 3: Determine Lane Width
      const aisleWidth = this.equipmentAisleWidths[this.equipmentType] || 0;
      const laneWidth = rackDepthFt + aisleWidth;
      
      // Step 4: Calculate Rack Rows
      const numberOfRows = Math.floor((this.buildingWidth || 0) / laneWidth);
      
      // Step 5: Total Linear Racking
      const totalLinearFeet = numberOfRows * (this.rackRunLength || 0);
      
      // Step 6: Usable Storage Volume
      const storageVolume = totalLinearFeet * rackDepthFt * rackHeightFt;
      
      // Step 7: Utilization %
      const utilizationPercent = buildingVolume > 0 ? (storageVolume / buildingVolume) * 100 : 0;
      
      this.results = {
        buildingVolume,
        storageVolume,
        utilizationPercent,
        numberOfRows,
        totalLinearFeet,
        rackHeightFt,
        rackDepthFt,
        aisleWidth,
        laneWidth
      };
      
      // Generate comparison results
      this.generateComparison();
    },

    generateComparison() {
      const equipmentTypes = ['manual', 'electric', 'reach', 'forklift'];
      const currentStorageVolume = this.results.storageVolume;
      
      this.comparisonResults = equipmentTypes.map(equipment => {
        const aisleWidth = this.equipmentAisleWidths[equipment];
        const rackDepthFt = (this.rackDepth || 0) / 12;
        const rackHeightFt = (this.rackHeight || 0) / 12;
        const laneWidth = rackDepthFt + aisleWidth;
        const numberOfRows = Math.floor((this.buildingWidth || 0) / laneWidth);
        const totalLinearFeet = numberOfRows * (this.rackRunLength || 0);
        const storageVolume = totalLinearFeet * rackDepthFt * rackHeightFt;
        const buildingVolume = (this.buildingLength || 0) * (this.buildingWidth || 0) * (this.ceilingHeight || 0);
        const utilizationPercent = buildingVolume > 0 ? (storageVolume / buildingVolume) * 100 : 0;
        
        // Calculate difference
        const diff = storageVolume - currentStorageVolume;
        const diffPercent = currentStorageVolume > 0 ? (diff / currentStorageVolume) * 100 : 0;
        
        let diffClass = '';
        let diffLabel = '';
        
        if (equipment === this.equipmentType) {
          diffClass = 'comparison-diff--current';
          diffLabel = 'Current';
        } else if (diff > 0) {
          diffClass = 'comparison-diff--positive';
          diffLabel = `+${this.formatNumber(Math.abs(diffPercent))}%`;
        } else if (diff < 0) {
          diffClass = 'comparison-diff--negative';
          diffLabel = `-${this.formatNumber(Math.abs(diffPercent))}%`;
        } else {
          diffClass = 'comparison-diff--neutral';
          diffLabel = '0%';
        }
        
        return {
          equipment,
          aisleWidth,
          numberOfRows,
          storageVolume,
          utilizationPercent,
          diffClass,
          diffLabel
        };
      });
    },

    reset() {
      this.buildingLength = null;
      this.buildingWidth = null;
      this.ceilingHeight = null;
      this.rackHeight = '';
      this.rackDepth = '';
      this.rackRunLength = null;
      this.equipmentType = '';
      this.results = null;
      this.comparisonResults = [];
    },

    handleEmailSubmit(userInfo) {
      this.userEmail = userInfo.email;
      this.showEmailModal = false;
      
      // Update the header component with the user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = userInfo.email;
        this.$refs.header.userName = userInfo.fullName;
        this.$refs.header.userPhone = userInfo.phone;
      }
      
      // If calculation was pending, perform it now
      if (this.pendingCalculation) {
        this.pendingCalculation = false;
        this.performCalculation();
      }
    },

    handleEmailCancel() {
      this.pendingCalculation = false;
      this.showEmailModal = false;
    },

    getEquipmentLabel(equipment) {
      const labels = {
        manual: 'Manual Pallet Jack',
        electric: 'Electric Pallet Jack',
        reach: 'Reach Truck',
        forklift: 'Standard Forklift'
      };
      return labels[equipment] || equipment;
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  }
};
</script>
